import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import AuthContext from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import ConfirmationModal from "../components/ConfirmationModal";
import { FaFileUpload, FaTrash, FaDownload } from "react-icons/fa";
import "./RecursoPages.css";

function DetalheRecurso() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [recurso, setRecurso] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
  const isAllowedFile = (f) => {
    const name = String(f?.name || "");
    const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
    const mime = String(f?.type || "").toLowerCase();
    const extOk = ext === "pdf" || ext === "png" || ext === "jpg" || ext === "jpeg";
    const mimeOk =
      mime === "application/pdf" || mime === "image/png" || mime === "image/jpeg";
    return extOk && mimeOk;
  };

  const uploadsBaseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(
    /\/api\/?$/,
    ""
  );

  const canManageAttachments = useMemo(() => {
    if (!recurso) return false;
    const perfil = Number(user?.perfil_id);
    // Somente o candidato (perfil 3) pode anexar/excluir anexos do próprio recurso.
    if (perfil !== 3) return false;
    const statusOk = recurso.status === "Pendente" || recurso.status === "Em Análise";
    const inicio = recurso.data_inicio_recurso ? new Date(recurso.data_inicio_recurso) : null;
    const fim = recurso.data_fim_recurso ? new Date(recurso.data_fim_recurso) : null;
    if (!inicio || !fim) return false;
    const agora = new Date();
    const withinWindow = agora >= inicio && agora <= fim;
    return statusOk && withinWindow;
  }, [recurso, user?.perfil_id]);

  const canDeleteRecurso = useMemo(() => {
    if (!recurso) return false;
    const perfil = Number(user?.perfil_id);
    if (perfil !== 3) return false;

    const statusOk = recurso.status === "Pendente" || recurso.status === "Em Análise";
    const inicio = recurso.data_inicio_recurso ? new Date(recurso.data_inicio_recurso) : null;
    const fim = recurso.data_fim_recurso ? new Date(recurso.data_fim_recurso) : null;
    if (!inicio || !fim) return false;
    const agora = new Date();
    return statusOk && agora >= inicio && agora <= fim;
  }, [recurso, user?.perfil_id]);

  useEffect(() => {
    let mounted = true;
    async function fetchRecurso() {
      setLoading(true);
      try {
        const { data } = await api.get(`/recursos/${id}`);
        if (!mounted) return;
        setRecurso(data);
      } catch (error) {
        addToast("Erro ao buscar recurso.", "error");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchRecurso();
    return () => {
      mounted = false;
    };
  }, [addToast, id]);

  const refresh = async () => {
    const { data } = await api.get(`/recursos/${id}`);
    setRecurso(data);
  };

  const handlePickAndUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = null;
    if (!picked.length) return;

    const invalid = picked.filter((f) => !isAllowedFile(f));
    const tooLarge = picked.filter((f) => Number(f?.size || 0) > MAX_FILE_SIZE);
    if (invalid.length) {
      addToast("Apenas PDF, PNG ou JPEG (JPG) são permitidos.", "error");
      return;
    }
    if (tooLarge.length) {
      addToast("Um dos arquivos excede 15MB (limite por arquivo).", "error");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append("anexos", f));
      await api.post(`/recursos/${id}/anexos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      addToast(`${picked.length} anexo(s) enviado(s).`, "success");
      await refresh();
    } catch (error) {
      addToast(error?.response?.data?.message || "Erro ao enviar anexos.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAnexo = async (anexoId) => {
    setDeletingId(anexoId);
    try {
      await api.delete(`/recursos/${id}/anexos/${anexoId}`);
      addToast("Anexo removido.", "success");
      await refresh();
    } catch (error) {
      addToast(error?.response?.data?.message || "Erro ao excluir anexo.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteRecurso = async () => {
    try {
      await api.delete(`/recursos/${id}`);
      addToast("Recurso excluído com sucesso.", "success");
      navigate("/meus-recursos");
    } catch (error) {
      addToast(error?.response?.data?.message || "Erro ao excluir recurso.", "error");
    } finally {
      setDeleteModalOpen(false);
    }
  };

  return (
    <>
      <PageHeader title="Detalhes do Recurso" />
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteRecurso}
        title="Excluir recurso"
        message="Tem certeza que deseja excluir este recurso? Esta ação não pode ser desfeita."
      />
      {loading ? (
        <p>Carregando...</p>
      ) : !recurso ? (
        <p>Recurso não encontrado.</p>
      ) : (
        <div className="recurso-page">
          <div className="surface-section">
            <h2 className="recurso-section-title">
              <span>
                {recurso.edital_titulo} - Edital Nº {recurso.numero_edital}/{recurso.ano_edital}
              </span>
              <span className="pill">{recurso.status}</span>
            </h2>

            <div className="recurso-meta" style={{ marginTop: 12 }}>
              <div>
                <strong>Cargo:</strong> {recurso.nome_cargo}
              </div>
              <div>
                <strong>Inscrição:</strong> {String(recurso.inscricao_id).padStart(4, "0")}
              </div>
            </div>

            <hr style={{ margin: "18px 0" }} />

            <p><strong>Decisão contestada</strong></p>
            <div className="recurso-pre">{recurso.decisao_contestada}</div>

            <p style={{ marginTop: 14 }}><strong>Argumentos</strong></p>
            <div className="recurso-pre">{recurso.argumentos}</div>

          {recurso.anexos_descricao ? (
            <>
              <p style={{ marginTop: 14 }}><strong>Descrição dos anexos</strong></p>
              <div className="recurso-pre">{recurso.anexos_descricao}</div>
            </>
          ) : null}

            <hr style={{ margin: "18px 0" }} />

            <div className="recurso-anexos">
              <p style={{ margin: 0 }}><strong>Anexos</strong></p>
              {Array.isArray(recurso.anexos) && recurso.anexos.length ? (
                <ul className="recurso-anexos__list">
                  {recurso.anexos.map((a) => (
                    <li key={a.id} className="recurso-anexos__item">
                      <div className="recurso-anexos__info">
                        <span className="recurso-anexos__name">{a.nome_arquivo}</span>
                        {a.hash_sha256 ? (
                          <span className="recurso-anexos__hash" title={a.hash_sha256}>
                            SHA-256: {String(a.hash_sha256).slice(0, 12)}…
                          </span>
                        ) : null}
                      </div>
                      <div className="recurso-anexos__actions">
                        <a
                          className="btn-modern btn-ghost"
                          href={`${uploadsBaseUrl}/uploads/${a.caminho_arquivo}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FaDownload />
                          <span>Baixar</span>
                        </a>
                        {canManageAttachments ? (
                          <button
                            type="button"
                            className="ghost-button button-circle"
                            onClick={() => handleDeleteAnexo(a.id)}
                            disabled={deletingId === a.id}
                            aria-label={`Excluir ${a.nome_arquivo}`}
                          >
                            <FaTrash />
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted-text">Nenhum anexo enviado.</p>
              )}

              {canManageAttachments ? (
                <div className="recurso-upload">
                  <div className="recurso-upload__row">
                    <div>
                      <strong>Adicionar anexos</strong>
                      <div className="recurso-upload__help">
                        Selecione arquivos e eles serão anexados automaticamente (até 15 arquivos, 15MB cada).
                      </div>
                    </div>
                    <div className="upload-controls">
                      <label className="btn-modern btn-file">
                        <FaFileUpload size={18} />
                        <span>{uploading ? "Enviando..." : "Escolher arquivos"}</span>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,image/png,image/jpeg,.jpg,.jpeg"
                          onChange={handlePickAndUpload}
                          disabled={uploading}
                          className="visually-hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

          </div>

          {recurso.resposta || recurso.medidas ? (
            <>
              <div className="surface-section">
                <h3 style={{ marginTop: 0 }}>Resposta do controlador</h3>
              {recurso.resposta ? (
                <>
                  <p><strong>Resposta</strong></p>
                  <div className="recurso-pre">{recurso.resposta}</div>
                </>
              ) : null}
              {recurso.medidas ? (
                <>
                  <p style={{ marginTop: 14 }}><strong>Medidas</strong></p>
                  <div className="recurso-pre">{recurso.medidas}</div>
                </>
              ) : null}
              </div>
            </>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            {canDeleteRecurso ? (
              <button
                type="button"
                className="btn-modern btn-danger"
                onClick={() => setDeleteModalOpen(true)}
              >
                <FaTrash />
                <span>Excluir recurso</span>
              </button>
            ) : null}
            <button
              className="button-secondary"
              onClick={() => navigate("/meus-recursos")}
            >
              Voltar
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default DetalheRecurso;
